// @flow
import React, { useContext, Fragment, useMemo } from 'react';
import classNames from 'classnames/bind';
import isEmpty from 'ramda/es/isEmpty';
import prop from 'ramda/es/prop';
import groupWith from 'ramda/es/groupWith';
import pipe from 'ramda/es/pipe';
import reduce from 'ramda/es/reduce';
import { useTranslation } from 'react-i18next';

import { toFixedValueCurrency } from 'app/common/lib/toFixedValue';
import EmptyWarn from 'app/common/layouts/EmptyWarn';
import { Price } from 'app/common/layouts/commonShop';

import {
  ECartItem,
  ECartItemThumb,
  ECartItemTitle,
  ECartItemContent,
  PerItem,
} from 'app/common/layouts/ecart';

import ItemStatus from '../../ItemStatus';
import { OrderContext } from '../../OrderData';

import styles from './AdminOrderGoods.module.scss';;


const cx = classNames.bind(styles);

type Props = {
  timezone: string,
}

const OrderGoods = ({ timezone }: Props) => {
  const { t } = useTranslation('shop');
  const {
    loading: { order: loadingData },
    content: { order },
  } = useContext(OrderContext);

  const currentStatuses = pipe(
    obj => prop('current_issue_statuses', obj) || [],
    reduce((acc, v) => ({ ...acc, ...v }), {}),
  )(order);

  const orderItems = useMemo(() => {
    const items = prop('order_items', order) || [];
    return groupWith(({ variant_id: vId1, id: id1 }, { variant_id: vId2, id: id2 }) => {
      const { datetime: dt1 = null } = currentStatuses[id1];
      const { datetime: dt2 = null } = currentStatuses[id2];
      return (vId1 === vId2 && dt1 === dt2);
    }, items);
  }, [order, currentStatuses]);

  if (isEmpty(orderItems) && !loadingData) {
    return (
      <EmptyWarn>
        {t('order_goods_fail')}
      </EmptyWarn>
    );
  }

  return (
    <Fragment>
      {orderItems.map(([{ id: itemId, image, name, price }, ...othersSameVariant]) => {
        const { status = 'not_issued', datetime } = currentStatuses[itemId];
        const quantity = othersSameVariant.length + 1;
        const itemPrice = `${toFixedValueCurrency(price)}/шт.`;
        const totalPrice = toFixedValueCurrency(price * quantity);
        const quantityText = `${quantity} шт.`;

        return (
          <ECartItem
            id={itemId}
            key={itemId}
            className={cx({ issued: status === 'issued' })}
          >
            <ECartItemThumb imgSrc={prop('standart', image)} title={name} />
            <ECartItemTitle className={styles.nameCol}>
              {name}
              <ItemStatus
                timezone={timezone}
                status={status}
                datetime={datetime}
              />
            </ECartItemTitle>
            <ECartItemContent>
              <Price>{quantityText}</Price>
              <PerItem className={styles.pricePerItem}>
                {itemPrice}
              </PerItem>
            </ECartItemContent>
            <ECartItemContent className={styles.priceCol}>
              <Price>{totalPrice}</Price>
            </ECartItemContent>
          </ECartItem>
        );
      })}
    </Fragment>
  );
};

export default OrderGoods;
